import { Component, OnDestroy } from '@angular/core';
import Highcharts from 'highcharts';
import { CacheService, ParameterHandlerService } from 'projects/common/src/app/services';
import { LanguageService } from 'projects/common/src/app/services/parameters';
import { getUniqueValues } from 'projects/common/src/app/services/utils/utilities';
import { IShift } from 'src/app/modules/core/interfaces';
import { DatePresentationPipe } from 'src/app/modules/core/pipes/date-presentation.pipe';
import { BooleanStateService } from 'src/app/modules/core/services/boolean-state.service';
import { MachineContextService } from 'src/app/modules/core/services/parameter/machine-context/machine-context.service';
import { StateSidebarService } from 'src/app/modules/sidebar/services/state-sidebar.service';
import { IParamsChart, IProductionTruckAndTolva } from 'src/app/modules/widget/interfaces/production-truck-and-tolva';
import { ScreenViewService } from 'src/app/modules/widget/services/screen-view.service';
import { CommunicationService } from 'src/app/services/communication';
import { DashboardWidgetChartComponent } from '../../dashboard-widget-chart/dashboard-widget-chart.component';

@Component({
  selector: 'app-production-by-truck-and-tolva',
  templateUrl: './production-by-truck-and-tolva.component.html',
  styleUrls: ['./production-by-truck-and-tolva.component.scss'],
})
export class ProductionByTruckAndTolvaComponent extends DashboardWidgetChartComponent implements OnDestroy {
  public truckAndTolvaList: IProductionTruckAndTolva[];
  public inputDate: any;
  public displayMessage = false;
  public hasData = false;
  public showLegend = false;
  public filterDelayOperative: any;
  public borderedColor = '';
  public series = [];

  constructor(
    protected progressBarService: BooleanStateService,
    protected communication: CommunicationService,
    protected parameterService: ParameterHandlerService,
    protected sidebarService: StateSidebarService,
    protected cacheService: CacheService,
    protected machineContext: MachineContextService,
    private languageService: LanguageService,
    private datePipe: DatePresentationPipe,
    protected screenViewService: ScreenViewService,
  ) {
    super(progressBarService, communication, parameterService, sidebarService, cacheService, machineContext, screenViewService);
    this.initRequestParams();
  }

  /**
   * Init request params
   */
  private initRequestParams(): void {
    this.params = {
      hasDate: true,
      hasShift: true,
      hasCompany: true,
      hasZone: true,
    };
  }
  /**
   * Draw chart status Park
   */
  public drawView(chartOptions?: any): void {
    this.series = [];
    const date = this.parameterService.parameters.item('date').value;
    const shift = this.parameterService.parameters.item('shift').value as IShift;

    const shiftTooltip = this.languageService.message('shift_').toUpperCase();
    const zoneTooltip = this.languageService.message('zone').toUpperCase();
    const loopsTranlation = this.languageService.message('transfers');
    const user = this.parameterService.parameters.item('user-data').value;
    const machineType = this.parameterService.parameters.item('machine-context').value || 'no disponible';

    if (chartOptions.length) {
      this.truckAndTolvaList = chartOptions;
      this.hasData = !!this.truckAndTolvaList;
      this.displayMessage = !!chartOptions.length;

      const modelTruck = this.truckAndTolvaList.map((tat) => {
        return { modelTruck: tat.modelTruck };
      });
      const zone = this.truckAndTolvaList.map((tat) => {
        return { zoneName: tat.zoneName };
      });

      const categoriesZone = this.getCategoriesZone(zone);
      const categoriesTruck = this.getCategoriesModelTruck(modelTruck);
      const categories = categoriesZone;

      const params = {
        date,
        shift,
        user,
        machineType,
        categoriesZone,
        loopsTranlation,
        zoneTooltip,
        shiftTooltip,
        categoriesTruck,
        categories,
        zone,
      };

      this.serieConfigChart(params);

      window.dispatchEvent(new Event('resize'));
    } else {
      this.displayMessage = false;
    }
  }

  public getCategoriesZone(zone: any) {
    return getUniqueValues(zone, 'zoneName').map((m) => m.zoneName);
  }
  public getCategoriesModelTruck(modelTruck: any) {
    return getUniqueValues(modelTruck, 'modelTruck').map((m) => m.modelTruck);
  }

  public getSerieDataDetailDay(): { modelTruck: string; zoneName: string; loopCount: number }[] {
    const groupedData = new Map<string, number>();

    this.truckAndTolvaList.forEach(({ modelTruck, zoneName, loopCount }) => {
      const key = `${modelTruck}-${zoneName}`;
      groupedData.set(key, (groupedData.get(key) || 0) + loopCount);
    });

    return Array.from(groupedData, ([key, loopCount]) => {
      const [modelTruck, zoneName] = key.split('-');
      return { modelTruck, zoneName, loopCount };
    });
  }

  private getBorderColor(color: string): string {
    switch (color) {
      case '#FFF3D8':
        return '#FFC23D';
      case '#FFFCCC':
        return '#FEEE00';
      case '#E5F1EC':
        return '#7CBAA1';
      default:
        return '';
    }
  }

  private serieConfigChart(params: IParamsChart): void {
    if (params.shift.shiftId === -1) {
      const result = this.getSerieDataDetailDay();

      for (const truck of params.categoriesTruck) {
        const color = this.truckAndTolvaList.find((f) => f.modelTruck === truck).colour;

        const serie = {
          name: truck,
          type: 'column',
          data: result.filter((f) => f.modelTruck === truck).map((m) => m.loopCount),
          borderWidth: 2,
          borderColor: this.getBorderColor(color),
          color,
          states: {
            hover: {
              color,
            },
          },
        };
        this.series.push(serie);
      }
    } else {
      for (const truck of params.categoriesTruck) {
        const color = this.truckAndTolvaList.find((f) => f.modelTruck === truck).colour;

        const serie = {
          name: truck,
          type: 'column',
          data: this.truckAndTolvaList.filter((f) => f.modelTruck === truck).map((m) => m.loopCount),
          borderWidth: 2,
          borderColor: this.getBorderColor(color),
          color,
          states: {
            hover: {
              color,
            },
          },
        };
        this.series.push(serie);
      }
    }
    const configChart = {
      chart: {
        plotBorderWidth: null,
        plotShadow: false,
        backgroundColor: '#F8F8F8',
        style: {
          fontFamily: 'Open Sans',
        },
      },
      subtitle: {
        text: `${this.languageService.message('company')}: ${params.user.company.name},
              ${this.languageService.message('device')}: ${this.languageService.message(params.machineType.name)},
              ${this.languageService.message('date')}: ${this.datePipe.transform(params.date)},
              ${this.languageService.message('shift_')}: ${params.shift.name}`,
      },
      title: {
        text: '',
        style: {
          fontSize: 12,
          fontWeight: '600',
          fontFamily: 'Open Sans',
          color: '#433E3E',
        },
      },
      credits: {
        enabled: false,
      },
      xAxis: {
        labels: {
          style: {
            color: '#707070',
          },
        },
        categories: params.categories,
        title: {
          text: this.languageService.message('zone').toUpperCase(),
          style: {
            fontSize: '12px',
            color: '#707070',
            fontWeight: '600',
          },
        },
      },
      yAxis: {
        labels: {
          style: {
            color: '#707070',
          },
        },
        title: {
          text: this.languageService.message('loops').toUpperCase(),
          style: {
            fontSize: '12px',
            color: '#707070',
            fontWeight: '600',
          },
        },
      },
      tooltip: {
        backgroundColor: 'rgb(51, 51, 51, 1)',
        borderColor: '#333333',
        borderRadius: 2,
        style: {
          color: '#FFFFFF',
        },
        useHTML: true,
        formatter(): string {
          let divSymbol = '';
          divSymbol = `<div style="background-color: ${this.color};
                          width: 30px;
                          height: 12px;
                          border: 1px solid ${this.color};
                          float: left;
                          margin-top: 2px">
                      </div>`;
          return `<div style="width: 180px;
                              font-size: 10px;
                              color: #FFFFFF;">
                    <strong style="font-weight: 700;">${params.shiftTooltip} ${params.shift.name.toUpperCase()}</strong> <br>
                    <span style="font-weight: 400;">${params.zoneTooltip} ${this.x}</span> <br>
                    ${divSymbol} &nbsp;
                    <span style="font-weight: 400;">${this.y} ${params.loopsTranlation}${this.y === 0 || this.y > 1 ? 's' : ''}</span>
                  </div>`;
        },
      },
      legend: {
        squareSymbol: false,
        align: 'center',
        verticalAlign: 'top',
        symbolHeight: 0.001,
        symbolWidth: 0.001,
        symbolRadius: 0.001,
        margin: 30,
        reversed: true,
        itemStyle: {
          fontSize: 12,
          fontWeight: '600',
          fontFamily: 'Open Sans',
          color: '#433E3E',
        },
        itemCheckboxStyle: {
          color: '#ffffff',
        },
        useHTML: true,
        labelFormatter(): string {
          return `
          <div style=" height: 20px; display: flex;" (click)="null">
            <svg width="36" height="18" style=" float: left;">
              <rect width="30" height="12" x="2" y="2"
              style="fill: ${this.color}; stroke-width: 2px !important;
              stroke: ${
                this.color === '#FFF3D8' ? '#FFC23D' : this.color === '#FFFCCC' ? '#FEEE00' : this.color === '#E5F1EC' ? '#7CBAA1' : ''
              } !important;"/>
            </svg>

            <span style="font-size: 12px; font-family: Open Sans; font-weight: 500;
            color: #433E3E; float: left;">${this.name}</span>
          </div>`;
        },
      },
      plotOptions: {
        series: {
          stacking: 'normal',
          events: {
            legendItemClick: (): boolean => {
              return false;
            },
          },
          maxPointWidth: 25,
          dataLabels: {
            enabled: true,
            allowOverlap: false,
            verticalAlign() {
              return this.y === 0 ? 'bottom' : 'top';
            },
            style: {
              color: '#707070',
              textOutline: 'none',
              fontWeight: '600',
              fontSize: '11px',
              fontFamily: 'Open Sans',
            },
            y: -20,
            formatter(): string {
              return this.point.y;
            },
          },
        },
        bar: {
          dataLabels: {
            enabled: true,
            formatter(): number | string {
              return this.y !== 0 ? this.y : '';
            },
          },
        },
      },
      series: this.series,
    };
    this.chart = Highcharts.chart(this.chartTarget.nativeElement, configChart as any, (chart) => {
      if (!this.hasData) {
        const newX = chart.plotWidth / 3 + chart.plotLeft;
        const newY = chart.plotHeight / 2 + chart.plotTop;
        chart.renderer
          .text('La solicitud no arrojó resultados', newX, newY)
          .css({
            color: '#4572A7',
            fontSize: '24px',
          })
          .add();
      }
    });
  }
}
